// Administração > Módulos > Assistente IA (BETA, pedido 2026-10-20): robô
// (nome + imagem), chave do ChatGPT (conversa/áudio/visão), apresentação da empresa, prompt base e as FUNÇÕES do
// agente — um agente só, cada função com o próprio prompt editável.
// Liberado no app só pra testadores enquanto estiver em beta. Backend:
// gpsdrive-backend src/ai-assistant/.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, KeyRound, Loader2, Plus, RotateCcw, Save, Trash2, Upload, X, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { AI_STYLES, AssistantAvatar, AssistantChat } from "@/components/ai-assistant/assistant-chat";
import { aiPalette } from "@/components/ai-assistant/ai-palette";
import { useAiAssistantAccess } from "@/components/ai-assistant/mobile-ai-assistant";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { cn } from "@/lib/utils";
import {
  fileToBase64,
  gpsDriveAiAssistantService,
  type AiAdminSettings,
  type AiFunction,
  type AiFunctionInput,
  type AiFunctionTool,
} from "@/services/gpsdrive-ai-assistant.service";

export const Route = createFileRoute("/admin/ai-assistant")({
  component: () => (
    <AdminAccessGate action="notificacoes">
      <AiAssistantAdminPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Assistente IA — Administração — Wp Rastreamento" }] }),
});

const ADMIN_KEY = ["ai-assistant", "admin"] as const;

type Form = Omit<
  AiAdminSettings,
  "avatarUrl" | "openaiKeyMasked" | "geminiKeyMasked" | "functions" | "usage" | "defaults"
> & { openaiKey?: string; geminiKey?: string; testerText: string };

const toForm = (s: AiAdminSettings): Form => ({
  enabled: s.enabled,
  audience: s.audience,
  testerEmails: s.testerEmails,
  testerText: s.testerEmails.join("\n"),
  name: s.name,
  welcomeMessage: s.welcomeMessage,
  companyPresentation: s.companyPresentation,
  basePrompt: s.basePrompt,
  openaiModel: s.openaiModel,
  geminiImageModel: s.geminiImageModel,
  dailyMessageLimit: s.dailyMessageLimit,
  themeColor: s.themeColor,
});

// "imagem" (Gemini) saiu em 2026-10-21 por custo — não aparece mais pra escolher.
const TOOL_LABEL: Partial<Record<AiFunctionTool, string>> = {
  none: "Só conversa (sem ferramenta)",
  agenda: "Agenda pessoal do cliente",
  gastos: "Controle de gastos do cliente",
  saude: "Saúde / personal do cliente (treino, água, refeições)",
};

const errMsg = (e: unknown) => (e instanceof GpsDriveApiError || e instanceof Error ? e.message : "Erro inesperado");

function AiAssistantAdminPage() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ADMIN_KEY, queryFn: () => gpsDriveAiAssistantService.getAdmin() });
  const [form, setForm] = useState<Form | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data && !form) setForm(toForm(settings.data));
  }, [settings.data, form]);

  const save = useMutation({
    mutationFn: (f: Form) =>
      gpsDriveAiAssistantService.updateAdmin({
        enabled: f.enabled,
        audience: f.audience,
        testerEmails: f.testerText
          .split(/[\s,;]+/)
          .map((e) => e.trim().toLowerCase())
          .filter(Boolean),
        name: f.name,
        welcomeMessage: f.welcomeMessage,
        companyPresentation: f.companyPresentation,
        basePrompt: f.basePrompt,
        openaiKey: f.openaiKey,
        openaiModel: f.openaiModel,
        geminiKey: f.geminiKey,
        geminiImageModel: f.geminiImageModel,
        dailyMessageLimit: f.dailyMessageLimit,
        themeColor: f.themeColor,
      }),
    onSuccess: (data) => {
      setSaveError(null);
      qc.setQueryData(ADMIN_KEY, data);
      setForm(toForm(data));
      qc.invalidateQueries({ queryKey: ["ai-assistant", "me"] });
    },
    onError: (e) => setSaveError(errMsg(e)),
  });

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => (f ? { ...f, [key]: value } : f));

  if (settings.isLoading || !form || !settings.data) {
    return (
      <div className="flex justify-center py-20">
        {settings.error ? (
          <p className="text-sm text-red-600">{errMsg(settings.error)}</p>
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        )}
      </div>
    );
  }
  const data = settings.data;

  const saveBar = (
    <div className="flex items-center justify-end gap-3">
      {saveError && <span className="text-sm text-red-600">{saveError}</span>}
      <Button onClick={() => save.mutate(form)} disabled={save.isPending}>
        {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        Salvar
      </Button>
    </div>
  );

  return (
    <div className="space-y-6 p-6" style={aiPalette(form.themeColor)}>
      <style>{AI_STYLES}</style>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <AssistantAvatar me={{ avatarUrl: data.avatarUrl, name: data.name }} size={52} />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">Assistente IA</h1>
              <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500">BETA</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Agente que conversa com os clientes no app. Em teste: só aparece para os testadores e para a equipe.
            </p>
          </div>
        </div>
        <div className="flex gap-6 text-sm">
          <Stat label="Mensagens (30 dias)" value={data.usage.messages30d} />
          <Stat label="Pessoas (30 dias)" value={data.usage.users30d} />
          <Stat label="Tokens (30 dias)" value={data.usage.tokens30d.toLocaleString("pt-BR")} />
        </div>
      </div>

      <GpsDriveSessionBanner />

      <Tabs defaultValue="geral">
        <TabsList className="flex h-auto flex-wrap justify-start">
          <TabsTrigger value="geral">Geral</TabsTrigger>
          <TabsTrigger value="chaves">Chaves de API</TabsTrigger>
          <TabsTrigger value="empresa">Empresa & personalidade</TabsTrigger>
          <TabsTrigger value="funcoes">Funções ({data.functions.length})</TabsTrigger>
          <TabsTrigger value="testar">Testar conversa</TabsTrigger>
        </TabsList>

        <TabsContent value="geral" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Liberação (beta)</CardTitle>
              <CardDescription>
                A equipe (admin/funcionário) sempre consegue testar. Clientes só veem o menu “Assistente (Beta)” no app
                com o assistente ligado.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                <div>
                  <div className="font-medium">Ligado para clientes</div>
                  <div className="text-sm text-muted-foreground">Desligado = só a equipe testa.</div>
                </div>
                <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} />
              </div>
              <div className="grid gap-2">
                <Label>Quem vê</Label>
                <Select value={form.audience} onValueChange={(v) => set("audience", v as Form["audience"])}>
                  <SelectTrigger className="max-w-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="testers">Somente clientes testadores (lista abaixo)</SelectItem>
                    <SelectItem value="all">Todos os clientes da empresa</SelectItem>
                  </SelectContent>
                </Select>
                {form.audience === "all" && (
                  <p className="text-sm text-amber-600">
                    Atenção: todos os clientes passam a ver o assistente, e cada mensagem consome a sua conta do ChatGPT.
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Label>E-mails/logins dos clientes testadores</Label>
                <Textarea
                  rows={4}
                  value={form.testerText}
                  onChange={(e) => set("testerText", e.target.value)}
                  placeholder={"cliente1@email.com\ncliente2@email.com"}
                />
                <p className="text-xs text-muted-foreground">Um por linha — o mesmo login que o cliente usa no app.</p>
              </div>
              <div className="grid gap-2 sm:max-w-xs">
                <Label>Limite de mensagens por pessoa por dia</Label>
                <Input
                  type="number"
                  min={1}
                  max={2000}
                  value={form.dailyMessageLimit}
                  onChange={(e) => set("dailyMessageLimit", Math.max(1, Number(e.target.value) || 1))}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Robô</CardTitle>
              <CardDescription>Nome, imagem/ícone e a primeira mensagem que o cliente vê.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <AvatarEditor avatarUrl={data.avatarUrl} name={form.name} />
              <div className="grid gap-2">
                <Label>Cor do assistente no app</Label>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="color"
                    value={form.themeColor ?? "#16a34a"}
                    onChange={(e) => set("themeColor", e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded-md border bg-transparent"
                    aria-label="Cor do assistente"
                  />
                  <Input
                    value={form.themeColor ?? ""}
                    placeholder="Cor da empresa"
                    onChange={(e) => set("themeColor", /^#[0-9a-fA-F]{6}$/.test(e.target.value) ? e.target.value : e.target.value ? form.themeColor : null)}
                    className="w-32 font-mono"
                  />
                  {form.themeColor && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => set("themeColor", null)}>
                      Usar a cor da empresa
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Cabeçalho, balões, botões e cartões do assistente usam esta cor.</p>
              </div>
              <div className="grid gap-2 sm:max-w-md">
                <Label>Nome do assistente</Label>
                <Input value={form.name} maxLength={60} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <Label>Mensagem de boas-vindas</Label>
                  <RestoreButton onClick={() => set("welcomeMessage", data.defaults.welcomeMessage)} />
                </div>
                <Textarea rows={3} value={form.welcomeMessage} onChange={(e) => set("welcomeMessage", e.target.value)} />
              </div>
            </CardContent>
          </Card>
          {saveBar}
        </TabsContent>

        <TabsContent value="chaves" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">ChatGPT (OpenAI) — conversa, fotos, PDFs e áudio</CardTitle>
              <CardDescription>
                Chave em platform.openai.com → API keys. Fica guardada criptografada e nunca é mostrada de novo.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <KeyField
                masked={data.openaiKeyMasked}
                value={form.openaiKey}
                placeholder="sk-..."
                onChange={(v) => set("openaiKey", v)}
              />
              <div className="grid gap-2">
                <Label>Modelo</Label>
                <Input value={form.openaiModel} onChange={(e) => set("openaiModel", e.target.value)} />
                <p className="text-xs text-muted-foreground">Ex.: gpt-4.1-mini (econômico), gpt-4.1, gpt-4o.</p>
              </div>
            </CardContent>
          </Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <TestKeys />
            {saveBar}
          </div>
        </TabsContent>

        <TabsContent value="empresa" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Apresentação da empresa</CardTitle>
              <CardDescription>
                Tudo que o assistente pode falar sobre a empresa: quem são, serviços, planos, horários, contatos,
                endereço, perguntas frequentes. Ele não inventa nada além disto.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={14}
                value={form.companyPresentation}
                onChange={(e) => set("companyPresentation", e.target.value)}
                placeholder={"Somos a WP Rastreamento, empresa de rastreamento veicular desde 2015...\nCentral: (00) 0000-0000, seg a sex das 8h às 18h...\nPlanos: ..."}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base">Personalidade (prompt base)</CardTitle>
                  <CardDescription>Regras gerais que valem para todas as funções: tom de voz, o que não fazer, etc.</CardDescription>
                </div>
                <RestoreButton onClick={() => set("basePrompt", data.defaults.basePrompt)} />
              </div>
            </CardHeader>
            <CardContent>
              <Textarea rows={12} value={form.basePrompt} onChange={(e) => set("basePrompt", e.target.value)} className="font-mono text-sm" />
            </CardContent>
          </Card>
          {saveBar}
        </TabsContent>

        <TabsContent value="funcoes">
          <FunctionsEditor functions={data.functions} />
        </TabsContent>

        <TabsContent value="testar">
          <TestChat />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function RestoreButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="ghost" size="sm" onClick={onClick}>
      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restaurar padrão
    </Button>
  );
}

function KeyField({
  masked,
  value,
  placeholder,
  onChange,
}: {
  masked: string | null;
  value: string | undefined;
  placeholder: string;
  onChange: (v: string | undefined) => void;
}) {
  const removing = value === "";
  return (
    <div className="grid gap-2">
      <Label>Chave de API</Label>
      <div className="flex items-center gap-2 text-sm">
        <KeyRound className="h-4 w-4 text-muted-foreground" />
        {removing ? (
          <span className="text-red-600">Será removida ao salvar</span>
        ) : masked ? (
          <span className="font-mono">{masked}</span>
        ) : (
          <span className="text-muted-foreground">Não cadastrada</span>
        )}
        {masked && !removing && (
          <Button type="button" variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => onChange("")}>
            Remover
          </Button>
        )}
        {removing && (
          <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => onChange(undefined)}>
            Desfazer
          </Button>
        )}
      </div>
      <Input
        type="password"
        autoComplete="off"
        value={removing ? "" : (value ?? "")}
        placeholder={masked ? "Colar nova chave para trocar" : placeholder}
        onChange={(e) => onChange(e.target.value.trim() ? e.target.value.trim() : undefined)}
      />
    </div>
  );
}

function TestKeys() {
  const test = useMutation({ mutationFn: () => gpsDriveAiAssistantService.testKeys() });
  const row = (label: string, r?: { ok: boolean; message: string }) =>
    r && (
      <div className="flex items-start gap-2 text-sm">
        {r.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" /> : <XCircle className="mt-0.5 h-4 w-4 text-red-600" />}
        <span>
          <strong>{label}:</strong> {r.message}
        </span>
      </div>
    );
  return (
    <div className="space-y-2">
      <Button type="button" variant="outline" onClick={() => test.mutate()} disabled={test.isPending}>
        {test.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Testar chaves salvas
      </Button>
      {test.error && <p className="text-sm text-red-600">{errMsg(test.error)}</p>}
      {row("ChatGPT", test.data?.openai)}
    </div>
  );
}

function AvatarEditor({ avatarUrl, name }: { avatarUrl: string | null; name: string }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ADMIN_KEY });
    qc.invalidateQueries({ queryKey: ["ai-assistant", "me"] });
  };
  const upload = useMutation({
    mutationFn: async (file: File) => gpsDriveAiAssistantService.uploadAvatar(file.type, await fileToBase64(file)),
    onSuccess: () => (setError(null), refresh()),
    onError: (e) => setError(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: () => gpsDriveAiAssistantService.removeAvatar(),
    onSuccess: refresh,
  });
  return (
    <div className="flex items-center gap-4">
      <AssistantAvatar me={{ avatarUrl, name }} size={72} />
      <div className="space-y-2">
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = "";
          }}
        />
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()} disabled={upload.isPending}>
            {upload.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
            Enviar imagem do robô
          </Button>
          {avatarUrl && (
            <Button type="button" variant="ghost" size="sm" onClick={() => remove.mutate()} disabled={remove.isPending}>
              <X className="mr-1 h-4 w-4" /> Remover
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">PNG/JPG quadrado, de preferência 512×512. Salva na hora.</p>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
}

const EMPTY_FUNCTION: AiFunctionInput = {
  name: "",
  emoji: "✨",
  description: "",
  prompt: "",
  tool: "none",
  enabled: true,
  sortOrder: 0,
};

function FunctionsEditor({ functions }: { functions: AiFunction[] }) {
  const [openId, setOpenId] = useState<string | "new" | null>(null);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          É um agente só: todas as funções ligadas entram nas instruções dele, e ele usa a certa conforme o pedido do
          cliente. Edite cada prompt separado para ir melhorando.
        </p>
        <Button onClick={() => setOpenId("new")}>
          <Plus className="mr-1.5 h-4 w-4" /> Nova função
        </Button>
      </div>
      {openId === "new" && (
        <FunctionCard
          initial={{ ...EMPTY_FUNCTION, sortOrder: functions.length }}
          onDone={() => setOpenId(null)}
        />
      )}
      {functions.map((f) =>
        openId === f.id ? (
          <FunctionCard key={f.id} id={f.id} initial={f} onDone={() => setOpenId(null)} />
        ) : (
          <button
            key={f.id}
            type="button"
            onClick={() => setOpenId(f.id)}
            className={cn(
              "flex w-full items-start gap-3 rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary/40",
              !f.enabled && "opacity-60",
            )}
          >
            <span className="text-2xl leading-none">{f.emoji}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{f.name}</span>
                {f.tool !== "none" && TOOL_LABEL[f.tool] && <Badge variant="secondary">{TOOL_LABEL[f.tool]}</Badge>}
                {!f.enabled && <Badge variant="outline">Desligada</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{f.description}</p>
              <p className="mt-1 line-clamp-2 font-mono text-xs text-muted-foreground/80">{f.prompt}</p>
            </div>
          </button>
        ),
      )}
    </div>
  );
}

function FunctionCard({ id, initial, onDone }: { id?: string; initial: AiFunctionInput; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<AiFunctionInput>({
    name: initial.name,
    emoji: initial.emoji,
    description: initial.description,
    prompt: initial.prompt,
    tool: initial.tool,
    enabled: initial.enabled,
    sortOrder: initial.sortOrder,
  });
  const [error, setError] = useState<string | null>(null);
  const done = () => {
    qc.invalidateQueries({ queryKey: ADMIN_KEY });
    qc.invalidateQueries({ queryKey: ["ai-assistant", "me"] });
    onDone();
  };
  const save = useMutation({
    mutationFn: () => (id ? gpsDriveAiAssistantService.updateFunction(id, f) : gpsDriveAiAssistantService.createFunction(f)),
    onSuccess: done,
    onError: (e) => setError(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: () => gpsDriveAiAssistantService.deleteFunction(id!),
    onSuccess: done,
    onError: (e) => setError(errMsg(e)),
  });
  const set = <K extends keyof AiFunctionInput>(k: K, v: AiFunctionInput[K]) => setF((x) => ({ ...x, [k]: v }));

  return (
    <Card className="border-primary/40">
      <CardContent className="space-y-4 pt-6">
        <div className="grid gap-4 sm:grid-cols-[80px_1fr_220px]">
          <div className="grid gap-2">
            <Label>Ícone</Label>
            <Input value={f.emoji} maxLength={4} onChange={(e) => set("emoji", e.target.value)} className="text-center text-xl" />
          </div>
          <div className="grid gap-2">
            <Label>Nome da função</Label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex.: Personal trainer" />
          </div>
          <div className="grid gap-2">
            <Label>Ferramenta</Label>
            <Select value={f.tool} onValueChange={(v) => set("tool", v as AiFunctionTool)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TOOL_LABEL) as AiFunctionTool[]).map((t) => (
                  <SelectItem key={t} value={t}>
                    {TOOL_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Quando usar (aparece também pro cliente como sugestão)</Label>
          <Input value={f.description} onChange={(e) => set("description", e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label>Prompt da função</Label>
          <Textarea rows={10} value={f.prompt} onChange={(e) => set("prompt", e.target.value)} className="font-mono text-sm" />
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={f.enabled} onCheckedChange={(v) => set("enabled", v)} /> Ligada
          </label>
          <label className="flex items-center gap-2 text-sm">
            Ordem
            <Input
              type="number"
              className="h-8 w-20"
              value={f.sortOrder}
              onChange={(e) => set("sortOrder", Math.max(0, Number(e.target.value) || 0))}
            />
          </label>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex flex-wrap justify-between gap-2">
          {id ? (
            <Button
              type="button"
              variant="ghost"
              className="text-red-600"
              onClick={() => window.confirm(`Excluir a função "${f.name}"?`) && remove.mutate()}
              disabled={remove.isPending}
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> Excluir
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onDone}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => save.mutate()} disabled={save.isPending || !f.name.trim()}>
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar função
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TestChat() {
  const me = useAiAssistantAccess();
  const [conversationId, setConversationId] = useState<string | null>(null);
  if (!me.data) return <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-muted-foreground" />;
  if (!me.data.allowed) return <p className="text-sm text-muted-foreground">Sem acesso ao assistente nesta conta.</p>;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
      <div className="flex h-[640px] flex-col overflow-hidden rounded-2xl border bg-slate-100 dark:bg-slate-950">
        <AssistantChat
          me={me.data}
          conversationId={conversationId}
          onConversationChange={setConversationId}
          header={
            <div className="flex items-center gap-2 border-b bg-white px-4 py-2.5 dark:bg-slate-900">
              <AssistantAvatar me={me.data} size={32} />
              <span className="font-semibold">{me.data.name}</span>
              <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500">BETA</Badge>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setConversationId(null)}>
                <Plus className="mr-1 h-4 w-4" /> Nova conversa
              </Button>
            </div>
          }
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Como testar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>Salve as mudanças antes — o teste usa o que está salvo.</p>
          <p>A agenda e os gastos anotados aqui ficam na SUA conta (aparecem no app em Perfil → Assistente).</p>
          <p>Ideias: “reunião amanhã 14h, me lembra 1h antes”, “gastei 80 no mercado”, foto de um prato, áudio, PDF.</p>
        </CardContent>
      </Card>
    </div>
  );
}
